// SGNL-222 — Data Hygiene tab chip vocabulary.
//
// The pre-SGNL-222 chip set (No summary / Silent 30d+ / Stalled 14d+ etc.)
// was sourced from `frontend/src/lib/signals.ts:SIGNAL_DEFS` and filtered
// rows by `r.active_signals[]` — a BACKEND-computed slug list. The row
// issue tags rendered in the table (e.g. "stale 351+ days", "missing
// amount") come from a SEPARATE FE-computed list in
// `aggregations.ts:computeHygieneBuckets`. The two vocabularies never
// intersected, so every chip returned zero matches against rows that
// clearly had hygiene issues — see SGNL-222.
//
// This module defines a chip vocabulary that mirrors what
// `computeHygieneBuckets` actually emits, so chip click → table filter
// returns the rows whose ISSUES column already shows that category.

import {
  HYGIENE_ISSUE_INVALID_MOD_DATE,
  HYGIENE_ISSUE_MISSING_AMOUNT,
  HYGIENE_ISSUE_MISSING_CLOSE_DATE,
  HYGIENE_ISSUE_MISSING_NEXT_STEP,
  HYGIENE_ISSUE_NO_MOD_DATE,
  type HygieneRow,
} from "./aggregations";

export interface HygieneFilterDef {
  slug: string;
  label: string;
  /** Returns true if this row should be included when the chip is selected. */
  matches: (row: HygieneRow) => boolean;
}

/** The chip set. Order is the render order. Each chip filters by a
 * predicate against the row's pre-computed `issues` list (or `age_bucket`
 * for the stale / old buckets, which are derivable from issue strings
 * but cheaper to read off the bucket field directly).
 *
 * Note on stale vs old chip overlap: `Stale 60d+` intentionally
 * INCLUDES `Old 120d+` rows (because 120 > 60). The `Old 120d+` chip
 * is a strict subset of `Stale 60d+`. This is the documented contract
 * — a user clicking `Stale 60d+` expects to see EVERY deal that's at
 * least 60 days idle, which by definition includes the old ones.
 * The header counter in `views/hygiene.ts` is named consistently
 * with this contract so the on-page numbers don't drift from the
 * chip badges. (Per architect-MED on this PR.)
 *
 * Adding a new chip: define the predicate to match against
 * `HygieneRow.issues` (use the `HYGIENE_ISSUE_*` constants from
 * `aggregations.ts` so the compiler enforces parity) or `age_bucket`
 * produced by `aggregations.ts:computeHygieneBuckets`. The chip will
 * render only when its count for the current dataset is > 0 (see
 * `countHygieneMatches`). */
export const HYGIENE_FILTERS: readonly HygieneFilterDef[] = [
  {
    slug: "stale",
    label: "Stale 60d+",
    matches: (r) => r.age_bucket === "stale" || r.age_bucket === "old",
  },
  {
    slug: "old",
    label: "Old 120d+",
    matches: (r) => r.age_bucket === "old",
  },
  {
    slug: "missing_amount",
    label: "Missing amount",
    matches: (r) => r.issues.includes(HYGIENE_ISSUE_MISSING_AMOUNT),
  },
  {
    slug: "missing_next_step",
    label: "Missing next step",
    matches: (r) => r.issues.includes(HYGIENE_ISSUE_MISSING_NEXT_STEP),
  },
  {
    slug: "missing_close_date",
    label: "Missing close date",
    matches: (r) => r.issues.includes(HYGIENE_ISSUE_MISSING_CLOSE_DATE),
  },
  {
    slug: "no_mod_date",
    label: "No mod date",
    matches: (r) =>
      r.issues.includes(HYGIENE_ISSUE_NO_MOD_DATE) ||
      r.issues.includes(HYGIENE_ISSUE_INVALID_MOD_DATE),
  },
];

/** Per-chip row counts for the current dataset. Returned as a slug→count
 * map so the renderer can decide which chips to show (count > 0) and
 * what badge to display next to each label. */
export function countHygieneMatches(
  rows: readonly HygieneRow[],
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const f of HYGIENE_FILTERS) {
    out[f.slug] = 0;
  }
  for (const r of rows) {
    for (const f of HYGIENE_FILTERS) {
      if (f.matches(r)) out[f.slug]++;
    }
  }
  return out;
}

/** Apply the active-chip selection to a hygiene-row set. Empty selection
 * → no filtering. Multi-select uses OR semantics (matches any selected
 * chip) — same as the legacy `activeSignals` filter so user mental
 * model stays consistent. */
export function filterByHygieneSelection(
  rows: readonly HygieneRow[],
  selected: ReadonlySet<string>,
): HygieneRow[] {
  if (selected.size === 0) return [...rows];
  const active = HYGIENE_FILTERS.filter((f) => selected.has(f.slug));
  // All slugs in `selected` are unknown (e.g. a stale URL param, a
  // typo in a programmatic caller, a chip that was removed in a
  // future refactor). Fall through to "show all rows" rather than
  // hiding everything — the safer default for an ambiguous filter.
  if (active.length === 0) return [...rows];
  return rows.filter((r) => active.some((f) => f.matches(r)));
}
